import React, { memo, useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import { ADMISSION_COLORS as C } from "../../constants/admissionTheme";
import { generateSessionId, getCrossDeviceUploadUrl } from "../../utils/crossDeviceQr";
import {
  fetchPatientDocumentStatus,
  generateUploadToken,
} from "../../api/documentService";

function CrossDeviceQrDialog({ open, onClose, onAutofill }) {
  const [sessionId, setSessionId] = useState(null);
  const [uploadToken, setUploadToken] = useState(null);
  const [checking, setChecking] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  const reset = useCallback(() => {
    setSessionId(null);
    setUploadToken(null);
    setChecking(false);
    setStatusMsg("");
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const openSession = useCallback(async () => {
    const id = generateSessionId();
    setSessionId(id);
    setUploadToken(null);
    setChecking(false);
    setStatusMsg("");
    const token = await generateUploadToken(id);
    if (token) setUploadToken(token);
  }, []);

  useEffect(() => {
    if (open) openSession();
    else reset();
  }, [open, openSession, reset]);

  const uploadUrl = sessionId ? getCrossDeviceUploadUrl(sessionId, uploadToken) : "";

  const checkUploadedDocument = async () => {
    if (!sessionId) return;
    setChecking(true);
    setStatusMsg("");
    try {
      const data = await fetchPatientDocumentStatus(sessionId);
      const procStatus = String(data?.processing_status || "").toLowerCase();

      if (procStatus === "completed" && data?.document_json) {
        onAutofill(data.document_json);
        toast.success("Document received — form autofilled!");
        handleClose();
        return;
      }

      if (procStatus === "failed" || procStatus === "error") {
        setStatusMsg("Document processing failed. Ask the patient to upload again.");
        return;
      }

      if (procStatus) {
        setStatusMsg("Document uploaded — still processing. Wait a moment and refresh.");
        return;
      }

      setStatusMsg("No document uploaded yet. Ask the patient to upload, then refresh.");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setStatusMsg("No document uploaded yet. Ask the patient to upload, then refresh.");
      } else {
        const serverMsg = err?.response?.data?.detail
          || err?.response?.data?.message
          || err?.message;
        setStatusMsg(serverMsg || "Could not check upload status. Please try again.");
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: `2px solid ${C.info.bg}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <QrCodeScannerIcon sx={{ color: C.primary }} />
          <Typography fontWeight={700} color={C.primary}>
            Scan QR to Upload Document
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ textAlign: "center", py: 3 }}>
        {sessionId && (
          <>
            <Typography variant="body2" color={C.textSecondary} mb={2}>
              Ask the patient to scan this QR code, pick a document type and upload it.
              Then tap <strong>Refresh</strong> below to fetch the details.
            </Typography>

            <Box
              component="a"
              href={uploadUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "inline-block",
                p: 2,
                border: `3px solid ${C.primary}`,
                borderRadius: 2,
                bgcolor: C.cardBg,
                mb: 1.5,
                textDecoration: "none",
              }}
            >
              <QRCodeSVG value={uploadUrl} size={220} level="L" includeMargin={false} />
            </Box>

            <Typography variant="caption" color={C.textMuted} display="block" mb={2}>
              Session: <code>{sessionId.slice(0, 8)}…</code>
            </Typography>

            <Button
              variant="contained"
              fullWidth
              disabled={checking}
              onClick={checkUploadedDocument}
              sx={{ py: 1.25 }}
            >
              {checking ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={18} color="inherit" />
                  Checking…
                </Box>
              ) : (
                "Refresh & Fetch Uploaded Document"
              )}
            </Button>

            {statusMsg && (
              <Typography
                variant="body2"
                color={C.textSecondary}
                sx={{ mt: 2, p: 1, bgcolor: C.pageBg, borderRadius: 1 }}
              >
                {statusMsg}
              </Typography>
            )}
          </>
        )}

        {!sessionId && (
          <Box py={4}>
            <CircularProgress size={28} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default memo(CrossDeviceQrDialog);
